import { useEffect, useState } from "react";
import moment from "moment";
import PlayerCardPopover from "./PlayerCardPopover";
import NationalityFlags from "../common/NationalityFlags";
import { resolveNationalities } from "../../utils/nationalityFlags";
import { getContractPill } from "../../utils/contractRadar";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import {
  Card,
  CardGrid,
  CardHeader,
  CardName,
  PersonAvatar,
  ContractStatusPill,
  PositionSubPill,
  ProgressBar,
  EmptyState,
  SkeletonBlock,
  OverflowButton,
  Button,
  PlusIcon,
  GlobeEmptyIcon,
} from "./v2";

// Players-tab grid — My Agency V2 rebuild (ARTBOARD-SPEC boards 2a default,
// 2b compact expiring view, 2c empty + skeleton, 2j mobile). PRESENTATION
// ONLY (Brief Rule 6): every handler below is the exact same prop wiring as
// before — onPlayerMenuClick (3-dot + "Add dates"), onPlayerClick (header →
// /search/player/{wyscout_id}), onChangeAgent ("Assign agent" on agentless
// cards → existing change-agent flow), popover matched on wyscout_id.
// Timing/format rules stay in existing logic (backend contract_urgency
// first, contractRadar fallback — Brief Rule 10).
//
// F-7 follow-up (boards 1a/2a): "Show all N players" ghost button —
// presentation-level truncation over the already-loaded board list
// (client slice; no pagination exists). `resetKey` (from the page's
// filter/search/sort state) collapses the grid again on any change.

// Board 2a shows six cards above the ghost "Show all …" button.
const COLLAPSED_COUNT = 6;

// Map the backend-derived contract_urgency {state,days,expires_on} to a pill.
// Root-cause correctness: prefer the server's derived state over recomputing
// client-side; fall back to contractRadar only when the field is absent.
const backendContractPill = (u) => {
  if (!u || !u.state) return null;
  switch (u.state) {
    case "expired":
      return { state: "expired", label: "Contract expired" };
    case "expiring":
      return {
        state: "soon",
        label: u.days != null ? `Expires in ${u.days} day${u.days === 1 ? "" : "s"}` : "Expiring soon",
      };
    case "neutral":
      return {
        state: "ok",
        label: u.expires_on ? `Expires ${moment(new Date(u.expires_on)).format("MMM YYYY")}` : "Under contract",
      };
    default:
      return { state: "none", label: "No contract data" };
  }
};

// contractRadar/backend states → ContractStatusPill statuses (2a family).
const PILL_STATUS = { expired: "expired", soon: "expiring", ok: "dated", none: "none" };

// Compute age from DOB, guarding against absent / impossible (future) dates.
const getAge = (dob) => {
  if (!dob) return null;
  const d = moment(new Date(dob));
  if (!d.isValid()) return null;
  const age = moment().diff(d, "years");
  if (age < 0 || age > 120) return null; // impossible row → treat as missing
  return age;
};

// Managed-by chip (2a: bg #F7FAFD border #EBF0F6 r10 padding 8px 12px,
// label 12.5/500, avatar 22 + name 13/600, hover per artboard; 2b compact =
// non-hover variant; 2j mobile = padding 10px 12px, label 13px).
// Agentless cards render the 1i unassigned row state (amber, "Assign agent"
// → existing change-agent flow) — see PARITY-FILL-PLAYERS.md supersession
// note; the All/Unassigned toolbar segment is NOT built (no existing logic).
const AgentChip = ({ agent, player, hover = true, mobile = false, canAssign, onAssign }) => {
  const name = agent?.name;
  if (!name) {
    return (
      <div
        className="flex items-center justify-between rounded-[10px] border border-[#FDE68A] bg-[#FFFBEB]"
        style={{ padding: mobile ? "10px 12px" : "8px 12px" }}
      >
        <span className={`font-medium text-[#92400E] ${mobile ? "text-[13px]" : "text-[12.5px]"}`}>
          Unassigned
        </span>
        {canAssign && (
          <button
            type="button"
            onClick={() => onAssign(player)}
            className="text-[13px] font-semibold text-[#B45309] whitespace-nowrap border-0 bg-transparent p-0 cursor-pointer rounded-md hover:bg-[#FEF3C7]"
          >
            Assign agent
          </button>
        )}
      </div>
    );
  }
  const avatar = agent.avatar_url || agent.profile_picture_url;
  return (
    <div
      className={`flex items-center justify-between rounded-[10px] border border-[#EBF0F6] bg-[#F7FAFD] ${
        hover ? "hover:border-[#B9D8FF] hover:bg-[#F0F7FF]" : ""
      }`}
      style={{ padding: mobile ? "10px 12px" : "8px 12px" }}
    >
      <span className={`font-medium text-[#64748B] ${mobile ? "text-[13px]" : "text-[12.5px]"}`}>
        Managed by
      </span>
      <span className="flex items-center gap-[7px] min-w-0">
        <PersonAvatar src={avatar} name={name} seed={agent.account_id || name} size={22} />
        <span className="text-[13px] font-semibold text-[#0F172A] whitespace-nowrap overflow-hidden text-ellipsis">
          {name}
        </span>
      </span>
    </div>
  );
};

// 2c skeleton card anatomy: header (44 circle + 58–64%×13 / 36–40%×10 bars)
// → pill 100–120×22 r999 → meta bar 62–70%×11 → agent-chip block h36 r10.
const SkeletonCard = ({ seed = 0 }) => (
  <Card gap={12} aria-hidden="true">
    <div className="flex gap-3 items-start">
      <SkeletonBlock circle height={44} tone="strong" />
      <div className="flex-1 flex flex-col gap-2 pt-1">
        <SkeletonBlock width={`${58 + (seed % 2) * 6}%`} height={13} tone="strong" />
        <SkeletonBlock width={`${36 + (seed % 2) * 4}%`} height={10} tone="light" />
      </div>
    </div>
    <SkeletonBlock width={100 + (seed % 2) * 20} height={22} radius={999} tone="chip" />
    <SkeletonBlock width={`${62 + (seed % 2) * 8}%`} height={11} tone="light" />
    <SkeletonBlock height={36} radius={10} tone="chip" />
  </Card>
);

const PlayersGrid = ({
  players,
  playersLoading,
  playersError,
  showPlayerPopover,
  popoverPlayer,
  onPlayerMenuClick,
  onClosePlayerPopover,
  onChangeAgent,
  onPlayerClick,
  onAddPlayer,
  hasActiveFilters = false,
  compact = false,
  isCurrentUserAdmin = false,
  resetKey = "",
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const pad = isMobile ? "px-4" : "px-6";

  // F-7 — expand state resets on any filter/search/sort change.
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    setShowAll(false);
  }, [resetKey]);

  if (playersLoading) {
    return (
      <div className={pad}>
        <CardGrid marginTop={18}>
          {[0, 1, 2, 3].map((i) => (
            <SkeletonCard key={i} seed={i} />
          ))}
        </CardGrid>
      </div>
    );
  }

  if (playersError) {
    return (
      <div className={`${pad} pt-[18px]`}>
        <div className="text-center py-12 text-red-600 bg-white border border-[#E3E9F1] rounded-xl">
          {playersError}
        </div>
      </div>
    );
  }

  if (!players || players.length === 0) {
    // Board 2c empty tab; a zero-result FILTER set has no artboard → the
    // existing "No players found" copy in the CMP-EMPTY small variant.
    return (
      <div className={`${pad} pt-[18px]`}>
        {hasActiveFilters ? (
          <EmptyState small title="No players found" />
        ) : (
          <EmptyState
            icon={<GlobeEmptyIcon />}
            title="No players yet"
            body="Add your first player to start pitching them to club requests and tracking their contracts."
            bodyMaxWidth={380}
            cta={
              onAddPlayer ? (
                <Button icon={<PlusIcon />} onClick={onAddPlayer}>
                  Add player
                </Button>
              ) : null
            }
          />
        )}
      </div>
    );
  }

  // F-7 slice — presentation only over the already-loaded board list.
  const visibleCards = showAll ? players : players.slice(0, COLLAPSED_COUNT);
  const truncated = !showAll && players.length > COLLAPSED_COUNT;

  return (
    <div className={pad}>
      <CardGrid marginTop={compact ? 12 : 18}>
        {visibleCards.map((player, index) => {
          const key = player.wyscout_id || player.id || index;
          // Prefer the backend-derived contract state; fall back to the client
          // radar only if the new field isn't present.
          const pill = backendContractPill(player.contract_urgency) || getContractPill(player.contract_expires_on);
          // Prefer server-computed age; guard invalid stored DOBs.
          const age = player.is_dob_valid === false ? null : (player.age ?? getAge(player.date_of_birth));
          const nat = player.nationality || player.country || null;
          // All citizenships (L-FE-1/L-FE-3): the backend's ordered `nationalities[]`, else the single field.
          const nats = resolveNationalities(player);
          const natList = nats.length ? nats : nat ? [nat] : [];
          const natNames = natList.map((n) => (typeof n === "string" ? n : n?.name)).filter(Boolean).join(" / ");
          const pos = player.tp_detailed_position || {};
          const primary = pos.primary_position || player.position || null;
          const secondary = pos.secondary_position || null;
          const tertiary = pos.teritory_position || null;
          const club = player.current_club_name || player.club_name || null;
          const managingAgent = player.managing_agent || null;
          const completion = player.completion || null;
          const completionPct = completion && completion.total
            ? Math.round((completion.complete_count / completion.total) * 100)
            : null;
          // Completion: use the server flag when present, else infer from a
          // profile that has neither a position nor a valid DOB.
          const incomplete = completion ? !completion.is_complete : (!primary && age == null);

          return (
            <Card
              key={key}
              gap={11}
              // Card-level hover per 2a (border #B9D8FF + lifted shadow);
              // navigation click stays on the HEADER only — the existing
              // wiring (widening the click target would be new behavior).
              className="relative hover:border-[#B9D8FF] hover:shadow-[0_4px_14px_rgba(15,23,42,.08)]"
            >
              {showPlayerPopover && popoverPlayer?.wyscout_id === player.wyscout_id && (
                <PlayerCardPopover
                  isOpen={showPlayerPopover}
                  onClose={onClosePlayerPopover}
                  player={popoverPlayer}
                  onChangeAgent={onChangeAgent}
                />
              )}

              {/* header: avatar · name · nationality + age (identity line
                  per 2a: flag 20×14 + "Country · NN yrs") */}
              <CardHeader
                identityGap={4}
                onClick={() => onPlayerClick(player?.wyscout_id)}
                style={{ cursor: "pointer" }}
                avatar={
                  <PersonAvatar
                    src={player.player_image || null}
                    name={player.name}
                    seed={player.wyscout_id || player.name}
                    size={44}
                  />
                }
                action={
                  isCurrentUserAdmin ? (
                    <OverflowButton
                      variant={isMobile ? "mobileOverflow" : "default"}
                      onClick={(e) => {
                        e.stopPropagation();
                        onPlayerMenuClick(player);
                      }}
                      aria-label="Player actions"
                    />
                  ) : null
                }
              >
                <CardName mobile={isMobile}>{player.name || "Unnamed player"}</CardName>
                {(natNames || age != null) && (
                  <span title={natNames || undefined} className="inline-flex items-center gap-1.5">
                    <NationalityFlags
                      nationalities={natList}
                      className="h-[12px] w-[17px] rounded-[2.5px] object-cover shadow-[inset_0_0_0_1px_rgba(17,24,39,.12)]"
                      wrapperClassName="flex flex-wrap items-center gap-1"
                    />
                    <span className="text-[12px] text-[#64748B]">
                      {[natNames, age != null ? `${age} yrs` : null].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                )}
              </CardHeader>

              {/* contract urgency pill (+ "Add dates" task link on no-data) */}
              <div className="flex items-center gap-2 flex-wrap">
                <ContractStatusPill status={PILL_STATUS[pill.state] || "dated"}>
                  {pill.label}
                </ContractStatusPill>
                {pill.state === "none" && isCurrentUserAdmin && (
                  <button
                    type="button"
                    onClick={() => onPlayerMenuClick(player)}
                    className="text-[12.5px] font-semibold text-[#1668D9] whitespace-nowrap border-0 bg-transparent p-0 cursor-pointer"
                  >
                    Add dates
                  </button>
                )}
              </div>

              {/* 2b compact cards drop the position/completion row entirely */}
              {!compact && (primary ? (
                <div className="flex items-center gap-[7px] flex-wrap min-w-0">
                  <span className="text-[13.5px] font-semibold text-[#0F172A] whitespace-nowrap">{primary}</span>
                  {secondary && <PositionSubPill>{secondary}</PositionSubPill>}
                  {tertiary && <PositionSubPill>{tertiary}</PositionSubPill>}
                  {club && (
                    <span className="text-[12px] text-[#64748B] whitespace-nowrap overflow-hidden text-ellipsis">{club}</span>
                  )}
                </div>
              ) : incomplete ? (
                // Profile-completeness task block (2a Mateo): missing data
                // becomes a task, never dash rows.
                <div className="flex flex-col gap-1.5 bg-[#F8FAFC] border border-[#EDF1F6] rounded-[10px] px-3 py-[9px]">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[12.5px] font-medium text-[#64748B] whitespace-nowrap">
                      {completionPct != null ? `Profile ${completionPct}%` : "Profile incomplete"}
                    </span>
                    <button
                      type="button"
                      onClick={() => onPlayerClick(player?.wyscout_id)}
                      className="text-[12.5px] font-semibold text-[#1668D9] whitespace-nowrap border-0 bg-transparent p-0 cursor-pointer"
                    >
                      {completion?.missing?.length ? `Add ${completion.missing[0]} →` : "Complete profile →"}
                    </button>
                  </div>
                  {completionPct != null && <ProgressBar percent={completionPct} />}
                </div>
              ) : null)}

              {/* managing-agent chip (2b: non-hover variant; agentless →
                  unassigned row state wired to the existing change-agent
                  flow) */}
              <AgentChip
                agent={managingAgent}
                player={player}
                hover={!compact}
                mobile={isMobile}
                canAssign={isCurrentUserAdmin}
                onAssign={onChangeAgent}
              />
            </Card>
          );
        })}
      </CardGrid>

      {/* F-7 — ghost "Show all N players" (board 2a): centered, mt 20. */}
      {truncated && (
        <div className="flex justify-center" style={{ marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setShowAll(true)}>
            Show all <span data-no-translate>{players.length} </span>
            {players.length === 1 ? "player" : "players"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PlayersGrid;
